import { useEffect, useState, type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";

import { Button } from "@/components/ui/button";
import { SignalWaitDot } from "@/motion/SignalWaitDot";

import { apiUrl, inboundAgentEmail, tenantPublicUrl } from "../lib/api";
import { authKitLoginUrl } from "./lib/auth-kit";
import { PreviewPassword, unlockUrl } from "./PreviewPassword";
import { MarketingLayout } from "./sections/Layout";

// Landing page after a successful Stripe Checkout redirect. The webhook is
// what flips the SITE row to active; this page just confirms the session was
// paid and shows the new owner where to go next. The welcome email is
// sent server-side from the webhook, not here.

interface SessionStatus {
  paid: boolean;
  slug: string | null;
  customerEmail: string | null;
  ready: boolean;
  launchStatus: "private" | "public" | null;
  previewPassword: string | null;
  siteUrl: string | null;
}

const AGENT_SMS = "+1 (888) 404-9410";

async function fetchSession(id: string): Promise<SessionStatus | null> {
  try {
    const r = await fetch(apiUrl(`/checkout/session/${id}`));
    if (!r.ok) return null;
    return (await r.json()) as SessionStatus;
  } catch {
    return null;
  }
}

/** Slug-only revisit. No Stripe session, so no receipt email or preview password. */
async function fetchSlugReady(slug: string): Promise<SessionStatus | null> {
  try {
    const r = await fetch(
      apiUrl(`/checkout/ready?slug=${encodeURIComponent(slug)}`),
    );
    if (!r.ok) return null;
    const data = (await r.json()) as {
      slug: string;
      ready: boolean;
      launchStatus: "private" | "public" | null;
      siteUrl: string;
    };
    return {
      paid: true,
      slug: data.slug,
      customerEmail: null,
      ready: data.ready,
      launchStatus: data.launchStatus,
      previewPassword: null,
      siteUrl: data.siteUrl,
    };
  } catch {
    return null;
  }
}

export function CheckoutSuccess(): ReactElement {
  const search = useSearch({ from: "/checkout-success" });
  const sessionId = search.session_id;
  const slugParam = search.slug;

  const [pollAttempts, setPollAttempts] = useState(0);
  const [timedOut, setTimedOut] = useState(false);

  // Activation runs async in the Stripe webhook AFTER this redirect, so the site
  // isn't live the instant we land here. Poll the session endpoint (which probes
  // the real edge) at a FIXED ~2s interval — latency-to-detect matters and the
  // endpoint is cheap, so exponential backoff would only make the owner wait
  // longer. Cap at ~80s, then fall back to "try again in a minute".
  const MAX_ATTEMPTS = 40; // ~80s at 2s
  const POLL_MS = 2_000;
  const sessionQuery = useQuery({
    queryKey: sessionId
      ? ["checkout-session", sessionId]
      : ["checkout-ready", slugParam],
    enabled: Boolean(sessionId || slugParam),
    queryFn: () =>
      sessionId ? fetchSession(sessionId) : fetchSlugReady(slugParam!),
    refetchInterval: (query) => {
      if (timedOut || pollAttempts >= MAX_ATTEMPTS || query.state.data?.ready) {
        return false;
      }
      return POLL_MS;
    },
    retry: false,
  });

  useEffect(() => {
    setPollAttempts(0);
    setTimedOut(false);
  }, [sessionId, slugParam]);

  useEffect(() => {
    if (!sessionQuery.dataUpdatedAt || sessionQuery.data?.ready) {
      return;
    }
    setPollAttempts((attempts) => {
      const nextAttempts = attempts + 1;
      if (nextAttempts >= MAX_ATTEMPTS) setTimedOut(true);
      return nextAttempts;
    });
  }, [sessionId, sessionQuery.dataUpdatedAt, sessionQuery.data?.ready]);

  const status = sessionQuery.data ?? null;
  const loading = Boolean(sessionId || slugParam) && sessionQuery.isPending;
  const slug = status?.slug ?? slugParam;
  const siteUrl = status?.siteUrl ?? (slug ? tenantPublicUrl(slug) : null);
  const siteHost = siteUrl ? siteUrl.replace(/^https?:\/\//, "") : null;
  const agentEmail = inboundAgentEmail();
  const email = status?.customerEmail;
  const workspaceUrl = slug ? authKitLoginUrl(slug) : authKitLoginUrl();

  const ready = status?.ready ?? false;
  const isPrivate = status?.launchStatus === "private";
  const previewPassword = status?.previewPassword ?? null;
  // For a private site, one-click unlock carries the password (CF strips it on
  // the 302); a public site links straight through.
  const goUrl =
    ready && siteUrl
      ? isPrivate && previewPassword
        ? unlockUrl(siteUrl, previewPassword)
        : siteUrl
      : null;

  return (
    <MarketingLayout ambient>
      <section className="mx-auto w-full max-w-xl px-5 py-16 sm:px-8 sm:py-24">
        <div className="rounded-[2rem] border border-white/80 bg-white/75 p-8 shadow-[0_18px_50px_rgba(34,73,66,0.1)] backdrop-blur-xl sm:p-10">
          {loading ? (
            <div className="flex items-center justify-center gap-2 text-sm text-[#65736f]">
              <SignalWaitDot className="bg-[#3d8e85]" />
              Confirming your subscription…
            </div>
          ) : (
            <>
              <div className="marketing-kicker text-center text-[#45635e]">
                You're in
              </div>
              <h1 className="marketing-display mt-3 text-center text-[2.4rem] leading-[1.05] text-[#12211d] sm:text-5xl">
                {ready ? "Your site is ready." : "Building your site"}
              </h1>

              {email ? (
                <p className="mt-4 text-center text-sm leading-6 text-[#5e6d69]">
                  Receipt went to{" "}
                  <span className="text-[#20272b]">{email}</span>.
                </p>
              ) : null}

              {slug && siteUrl ? (
                <p className="mt-5 text-center">
                  <span className="block text-xs uppercase tracking-[0.16em] text-[#7a8a86]">
                    Your site
                  </span>
                  {goUrl ? (
                    <a
                      href={goUrl}
                      className="mt-1 inline-block text-xl text-[#20272b] underline decoration-[#b8b9dd] underline-offset-4 hover:decoration-[#20272b]"
                    >
                      {siteHost}
                    </a>
                  ) : (
                    <span className="mt-1 inline-block text-xl text-[#20272b]">
                      {siteHost}
                    </span>
                  )}
                </p>
              ) : null}

              {!ready && !timedOut ? (
                <p className="mt-5 flex items-center justify-center gap-2 text-sm text-[#65736f]">
                  <SignalWaitDot className="bg-[#3d8e85]" />
                  This takes a few seconds.
                </p>
              ) : null}
              {timedOut && !ready ? (
                <p className="mt-5 text-center text-sm leading-6 text-[#65736f]">
                  This is taking longer than usual. Your site will be live in a
                  minute.
                </p>
              ) : null}

              {ready && isPrivate && previewPassword ? (
                <div className="mt-8 rounded-2xl border border-[#d5e9f7] bg-[#f3f9fe] p-4">
                  <p className="text-sm font-medium text-[#172722]">
                    Private while you set it up
                  </p>
                  <p className="mb-3 mt-1 text-sm leading-6 text-[#65736f]">
                    “Go to your site” unlocks it for you. Share this password
                    to let others preview it.
                  </p>
                  <PreviewPassword password={previewPassword} />
                </div>
              ) : null}

              <div className="mt-8 rounded-2xl border border-[#d5e9f7] bg-[#f3f9fe] p-5">
                <p className="text-sm font-medium text-[#172722]">
                  How to update it
                </p>
                <p className="mt-2 text-sm leading-6 text-[#65736f]">
                  Text or email Chill the way you would a person. Hours, menus,
                  photos, dates — another message, and it stays handled.
                </p>
                <ul className="mt-4 space-y-2 text-sm text-[#536762]">
                  <li>
                    <a
                      href={`mailto:${agentEmail}`}
                    className="text-[#20272b] underline decoration-[#b8b9dd] underline-offset-2 hover:decoration-[#20272b]"
                    >
                      {agentEmail}
                    </a>
                  </li>
                  <li>
                    <span className="text-[#20272b]">{AGENT_SMS}</span>
                  </li>
                </ul>
              </div>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-center">
                {goUrl ? (
                  <Button
                    asChild
                    className="h-11 rounded-full bg-[#dceffc] px-6 text-sm text-[#20272b] shadow-none hover:bg-[#c8e6fa]"
                  >
                    <a href={goUrl}>Go to your site</a>
                  </Button>
                ) : (
                  <Button
                    disabled
                    className="h-11 rounded-full bg-[#dceffc] px-6 text-sm text-[#20272b] shadow-none disabled:opacity-55"
                  >
                    {timedOut ? "Go to your site" : "Preparing…"}
                  </Button>
                )}
                {ready || timedOut ? (
                  <Button
                    asChild
                    variant="outline"
                    className="h-11 rounded-full border-[#c7e2f5] bg-white px-6 text-sm text-[#20272b] shadow-none hover:bg-[#f3f9fe]"
                  >
                    <a href={workspaceUrl}>Open your workspace</a>
                  </Button>
                ) : null}
              </div>
              <p className="mt-5 text-center">
                <Link
                  to="/"
                  className="text-sm text-[#7a8a86] underline decoration-transparent underline-offset-4 hover:text-[#20272b] hover:decoration-[#b8b9dd]"
                >
                  Back to home
                </Link>
              </p>
            </>
          )}
        </div>
      </section>
    </MarketingLayout>
  );
}
